import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import { useComputeCredit } from '../../context/ComputeCreditContext.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import { backtestApi } from '../../lib/apiClient.js';
import { loadLastRun, saveLastRun } from '../../utils/lastRun.js';
import { resolveRunTab, isLowCredit, shouldRefreshHistoryForRun, historyMetaText } from '../../utils/backtestStudio.js';
import { DEFAULT_CONFIG } from './ConfigPanel.jsx';
import RulesBuilderMode from './modes/RulesBuilderMode.jsx';
import SignalsMode from './modes/SignalsMode.jsx';
import MlResearchMode from './modes/MlResearchMode.jsx';
import OptimizeMode from './modes/OptimizeMode.jsx';
import SearchMode from './modes/SearchMode.jsx';
import MonteCarloMode from './modes/MonteCarloMode.jsx';
import WalkForwardMode from './modes/WalkForwardMode.jsx';
import PortfolioMode from './modes/PortfolioMode.jsx';
import TrackRecord from './TrackRecord.jsx';
import EquityCurveChart from './EquityCurveChart.jsx';
import { engineConfig } from '../../utils/engineConfig.js';
import { migrateBacktestPeriod } from '../../utils/backtestPeriod.js';

const MODES = [
  { key: 'rules', label: 'Rules builder', Component: RulesBuilderMode },
  { key: 'signals', label: 'Signals', Component: SignalsMode },
  { key: 'ml', label: 'ML research', Component: MlResearchMode },
  { key: 'optimize', label: 'Optimize', Component: OptimizeMode },
  { key: 'search', label: 'Search', Component: SearchMode },
  { key: 'montecarlo', label: 'Monte-Carlo', Component: MonteCarloMode },
  { key: 'walkforward', label: 'Walk-forward', Component: WalkForwardMode },
  { key: 'portfolio', label: 'Portfolio', Component: PortfolioMode },
];

const MODE_LABELS = Object.fromEntries(MODES.map((m) => [m.key, m.label]));

/**
 * Backtest Studio — the mode-tabbed research workspace fronting the real
 * open_backtester engine (docs/mystocks/OBT_INTEGRATION_SPEC.md §5). Common
 * config (universe/symbols, period, capital, sizing, sl/pt/max-hold,
 * CPU/GPU compute) is shared across every tab so switching modes doesn't
 * lose the user's setup. Each mode owns its own strategy/signals/ML/grid
 * inputs and results panel.
 *
 * Run history is server-side (GET /api/backtest/history — the backend
 * persists every run per seat). A "My backtests" list lets the user re-open
 * a past run (GET /api/backtest/:jobId) after navigating away or reloading.
 * localStorage (utils/lastRun.js) is a fast offline cache for the most
 * recent per-mode result, but the server is the source of truth.
 */
export default function BacktestWorkspace() {
  const location = useLocation();
  const { user } = useAuth();
  const [lastRun, setLastRun] = useState(() => loadLastRun(user?.uid));
  const [mode, setMode] = useState(lastRun?.lastMode || 'rules');
  const [config, setConfig] = useState(() => {
    if (lastRun?.config) return migrateBacktestPeriod(lastRun.config);
    const prefillSymbols = location.state?.symbols;
    return prefillSymbols?.length ? { ...DEFAULT_CONFIG, symbols: prefillSymbols } : DEFAULT_CONFIG;
  });
  const [rulesStrategy, setRulesStrategy] = useState(() => location.state?.spec?.strategy || null);
  const { wallet, refresh } = useComputeCredit();

  const [history, setHistory] = useState(null); // null = not loaded yet
  const [historyError, setHistoryError] = useState('');
  const [loadingRun, setLoadingRun] = useState(false);

  // Copied strategy preload (from the Strategy Marketplace "Copy & Run").
  const copied = location.state?.spec ? { title: location.state.strategyTitle, spec: location.state.spec } : null;
  const [copiedResult, setCopiedResult] = useState(null);
  const [copiedRunning, setCopiedRunning] = useState(false);
  const [copiedError, setCopiedError] = useState('');

  async function runCopied() {
    if (!copied) return;
    setCopiedRunning(true);
    setCopiedError('');
    try {
      const data = await backtestApi.run({ ...copied.spec, config: engineConfig(config), symbols: config.symbols, period: config.period });
      setCopiedResult(data);
      refresh();
      handleSaveRun(data);
    } catch (err) {
      setCopiedError(err.message || 'Failed to run the copied strategy.');
    } finally {
      setCopiedRunning(false);
    }
  }

  // Also seed the shared config from the copied spec so the other tabs inherit
  // its universe/period/capital without the user re-entering them.
  useEffect(() => {
    if (!copied) return;
    const spec = copied.spec;
    setConfig((prev) => ({
      ...prev,
      ...(spec.symbols?.length ? { symbols: spec.symbols } : {}),
      ...(spec.period?.from && spec.period?.to ? { period: { from: spec.period.from, to: spec.period.to } } : {}),
      ...(spec.config?.capital ? { capital: spec.config.capital } : {}),
      ...(spec.config?.max_positions ? { maxPositions: spec.config.max_positions } : {}),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = useCallback(async () => {
    if (!user) {
      setHistory([]);
      return;
    }
    setHistoryError('');
    try {
      const rows = await backtestApi.history();
      setHistory(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setHistoryError(err.message || 'Failed to load backtest history.');
      setHistory([]);
    }
  }, [user]);

  useEffect(() => {
    refresh();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // current mode's persisted result (offline cache)
  const currentResult = lastRun?.results?.[mode] ?? null;

  function handleSaveRun(result) {
    if (!result) return;
    setLastRun((prev) => {
      const base = prev || {};
      const next = {
        lastMode: mode,
        config,
        results: { ...(base.results || {}), [mode]: result },
      };
      saveLastRun(user?.uid, next);
      return next;
    });
    // Refresh once when the run becomes terminal. Active-run polling updates
    // the local result without repeatedly fetching the full history list.
    if (shouldRefreshHistoryForRun(result)) loadHistory();
  }

  async function openRun(jobId) {
    if (!jobId) return;
    setLoadingRun(true);
    try {
      const data = await backtestApi.get(jobId);
      // Guess the mode from the run's meta (mode for plain runs, runType for
      // derivatives); fall back to the current tab.
      const targetMode = resolveRunTab(data?.meta, mode, MODES.map((m) => m.key));
      setMode(targetMode);
      // Seed the offline cache so the mode component restores it on mount.
      setLastRun((prev) => {
        const base = prev || {};
        const next = { ...base, lastMode: targetMode, config, results: { ...(base.results || {}), [targetMode]: data } };
        saveLastRun(user?.uid, next);
        return next;
      });
    } catch (err) {
      setHistoryError(err.message || 'Failed to load that run.');
    } finally {
      setLoadingRun(false);
    }
  }

  useEffect(() => {
    const id = new URLSearchParams(location.search).get('run');
    if (id && user) openRun(id);
    // The URL is the durable navigation reference; results are owner-scoped server-side.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, user?.uid]);

  useEffect(() => {
    if (!currentResult?.jobId || ['complete', 'error', 'failed', 'cancelled'].includes(currentResult.status)) return;
    let alive = true;
    const timer = setInterval(async () => {
      try {
        const data = await backtestApi.get(currentResult.jobId);
        if (alive) handleSaveRun(data);
      } catch (error) { if (alive) setHistoryError(error.message || 'Status temporarily unavailable.'); }
    }, 3000);
    return () => { alive = false; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentResult?.jobId, currentResult?.status, mode]);

  const active = MODES.find((m) => m.key === mode) || MODES[0];
  const ActiveComponent = active.Component;

  const lowCredit = isLowCredit(wallet);

  return (
    <>
      <Card>
        <h1 className="heading" style={{ fontSize: 22 }}>
          Backtest Studio
        </h1>
        <p className="aq-module-desc">
          Rules, signals, ML research, optimization, Monte-Carlo, walk-forward, and multi-strategy portfolio analysis —
          powered by open_backtester. Data source, price basis, currency and research assumptions accompany each run;
          technology layer only, not advice.
        </p>
      </Card>

      {lowCredit && (
        <div className="aq-credit-banner">
          <p>
            Your compute credit is <strong>${wallet.balanceUsd.toFixed(2)}</strong> — runs will soon be blocked.
            Add credits or upgrade to keep backtesting.
          </p>
          <Button onClick={() => (window.location.href = '/app/billing')}>Add credits</Button>
        </div>
      )}

      {copied && (
        <Card className="aq-history">
          <h3 className="heading" style={{ fontSize: 16, marginBottom: 8 }}>
            Copied strategy: {copied.title}
          </h3>
          <p className="aq-module-desc">
            Loaded from the Strategy Marketplace. Symbols:{' '}
            <strong className="num">{(copied.spec.symbols || []).join(', ')}</strong> · period{' '}
            <strong className="num">
              {copied.spec.period?.from} → {copied.spec.period?.to}
            </strong>
            . Run the exact strategy below, or edit it in the tabs above first.
          </p>
          <Button onClick={runCopied} disabled={copiedRunning}>
            {copiedRunning ? 'Running…' : 'Run this strategy'}
          </Button>{' '}
          {copiedError && <span className="aq-news-meta" style={{ color: 'var(--aq-error, #ce2a2a)' }}>{copiedError}</span>}
          {copiedResult && (
            <div style={{ marginTop: 12 }}>
              <EquityCurveChart equity={copiedResult.equity || []} label={copied.title} />
              {copiedResult.metrics?.overall && (
                <div className="aq-stat-row">
                  <span className="aq-stat">
                    Total return <strong className="num">{(copiedResult.metrics.overall.totalReturn ?? 0).toFixed(2)}%</strong>
                  </span>
                  <span className="aq-stat">
                    Sharpe <strong className="num">{(copiedResult.metrics.overall.sharpe ?? 0).toFixed(2)}</strong>
                  </span>
                  <span className="aq-stat">
                    Max drawdown <strong className="num">{(copiedResult.metrics.overall.maxDrawdown ?? 0).toFixed(2)}%</strong>
                  </span>
                  <span className="aq-stat">
                    Trades <strong className="num">{copiedResult.metrics.overall.trades ?? 0}</strong>
                  </span>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      <div className="aq-tabbar" role="tablist" aria-label="Backtest mode">
        {MODES.map((m) => (
          <button
            key={m.key}
            role="tab"
            aria-selected={mode === m.key}
            className={`aq-tab ${mode === m.key ? 'aq-tab--active' : ''}`}
            onClick={() => setMode(m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>

      {currentResult?.status && currentResult.status !== 'complete' && <p role="status" className="aq-note">
        Run {currentResult.status}. {currentResult.error || 'Progress is also available in My jobs on every page.'}
      </p>}
      <ActiveComponent
        config={config}
        onConfigChange={setConfig}
        rulesStrategy={rulesStrategy}
        onRulesStrategyChange={setRulesStrategy}
        wallet={wallet}
        onRan={refresh}
        lastRun={{ result: currentResult }}
        onSaveRun={handleSaveRun}
      />

      <Card className="aq-history">
        <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
          My backtests
        </h3>
        {historyError && <p className="aq-auth-alert aq-auth-alert--error">{historyError}</p>}
        {history === null && <p className="aq-module-desc">Loading history…</p>}
        {history && history.length === 0 && !historyError && (
          <p className="aq-module-desc">No backtests yet. Run one above and it will appear here — saved on the server so it survives navigation and reload.</p>
        )}
        {history && history.length > 0 && (
          <div className="aq-history-list">
            {history.map((r) => (
              <button
                key={r.jobId}
                className="aq-history-row"
                onClick={() => openRun(r.jobId)}
                disabled={loadingRun}
                title={`Open ${r.jobId}`}
              >
                <span className="aq-history-mode">{MODE_LABELS[r.mode] || r.mode || 'run'}</span>
                <span className="aq-history-date">{r.savedAt ? new Date(r.savedAt).toLocaleString() : ''}</span>
                <span className="aq-history-meta">
                  {historyMetaText(r)}
                  {r.symbols?.length ? ` · ${r.symbols.slice(0, 3).join(', ')}${r.symbols.length > 3 ? '…' : ''}` : ''}
                </span>
              </button>
            ))}
          </div>
        )}
      </Card>

      <TrackRecord />
    </>
  );
}
